import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PILLARS = ["S", "P", "A", "C", "E"];
const PILLAR_NAMES = {
  S: "Spiritual",
  P: "Psychological",
  A: "Authentic",
  C: "Christ",
  E: "Entrepreneur Energy",
};
const PILLAR_DESCRIPTIONS = {
  S: "Connection to Source, intuition, divine guidance, spiritual identity.",
  P: "Emotional intelligence, mental clarity, internal narratives, identity architecture.",
  A: "Identity congruence, truth, alignment, self-expression.",
  C: "Covenant identity · apostolic instruction · mystical essence · Christ embodiment.",
  E: "Vitality, frequency and presence — and the work and offers that carry your gift.",
};

const LIKERT = [
  { value: 1, short: "SD", label: "Strongly Disagree" },
  { value: 2, short: "D", label: "Disagree" },
  { value: 3, short: "N", label: "Neutral" },
  { value: 4, short: "A", label: "Agree" },
  { value: 5, short: "SA", label: "Strongly Agree" },
];

export default function Diagnostic() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | invalid | used | submitting | done | error
  const [meta, setMeta] = useState(null); // { name, email, questions }
  const [pillarIdx, setPillarIdx] = useState(0); // 0..4
  const [responses, setResponses] = useState({ S: [], P: [], A: [], C: [], E: [] });
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    document.title = "S.P.A.C.E. Academy Diagnostic Test";
    (async () => {
      try {
        const res = await axios.get(`${API}/diagnostic/validate`, { params: { token } });
        setMeta(res.data);
        setStatus("ready");
      } catch (e) {
        const s = e?.response?.status;
        if (s === 410) setStatus("used");
        else if (s === 404) setStatus("invalid");
        else setStatus("error");
      }
    })();
  }, [token]);

  function chooseOption(pillar, qIndex, value) {
    const next = { ...responses };
    const arr = [...(next[pillar] || [])];
    while (arr.length < 12) arr.push(null);
    arr[qIndex] = value;
    next[pillar] = arr;
    setResponses(next);
  }

  function isPillarComplete(pillar) {
    const arr = responses[pillar] || [];
    return arr.length === 12 && arr.every((v) => v >= 1 && v <= 5);
  }

  function goNextPillar() {
    if (pillarIdx < PILLARS.length - 1) {
      setPillarIdx(pillarIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
  function goPrevPillar() {
    if (pillarIdx > 0) {
      setPillarIdx(pillarIdx - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function submit() {
    setErrorMsg("");
    for (const p of PILLARS) {
      if (!isPillarComplete(p)) {
        setErrorMsg(`Please answer all 12 questions in the ${PILLAR_NAMES[p]} pillar.`);
        // Jump to that pillar
        setPillarIdx(PILLARS.indexOf(p));
        return;
      }
    }
    setStatus("submitting");
    try {
      const res = await axios.post(`${API}/diagnostic/submit`, {
        token,
        responses,
      });
      setResult(res.data);
      setStatus("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Submission failed. Please try again.");
      setStatus("ready");
    }
  }

  // ============================================================
  // Render
  // ============================================================
  if (status === "loading") {
    return (
      <div className="space-diag" data-testid="diag-loading">
        <div className="diag-wrap">
          <div className="diag-loading">Loading your diagnostic…</div>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="space-diag" data-testid="diag-invalid">
        <div className="diag-wrap">
          <div className="diag-error-card">
            <h2>Invalid Access Link</h2>
            <p>This diagnostic link is not recognized. Please contact Dr. Rogers at masterteacher33.com if you've recently donated and need access.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "used") {
    return (
      <div className="space-diag" data-testid="diag-used">
        <div className="diag-wrap">
          <div className="diag-error-card">
            <h2>This Link Has Already Been Used</h2>
            <p>Your S.P.A.C.E. Identity Map was already generated and emailed. Check your inbox (and spam) for the PDF. If you can't find it, contact Dr. Rogers and he'll re-send it.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="space-diag" data-testid="diag-error">
        <div className="diag-wrap">
          <div className="diag-error-card">
            <h2>Something went wrong</h2>
            <p>We couldn't load your diagnostic. Please refresh — if the issue persists, contact Dr. Rogers.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "done" && result) {
    return (
      <div className="space-diag" data-testid="diag-done">
        <div className="diag-wrap">
          <div className="diag-done-card">
            <div className="diag-kicker">Diagnostic Complete · Identity Map Delivered</div>
            <h1>
              {result.name}, your map is in your inbox.
            </h1>
            <p className="diag-done-sub">
              Overall: <span className="gold">{result.overall_tier_label}</span> · {result.total_score} / 300
            </p>
            <div className="diag-summary-grid">
              {PILLARS.map((p) => (
                <div className="diag-summary-card" key={p}>
                  <div className="diag-summary-letter">{p}</div>
                  <div className="diag-summary-name">{PILLAR_NAMES[p]}</div>
                  <div className="diag-summary-score">{result.pillar_scores[p]} / 60</div>
                  <div className="diag-summary-tier">{result.pillar_tiers[p]}</div>
                </div>
              ))}
            </div>
            <p className="diag-done-note">
              Your full Identity Map PDF — including bar charts, per-pillar interpretation,
              and your custom alignment protocol — has been sent to <strong>{result.email}</strong>.
              Check spam if you don't see it within 5 minutes.
            </p>
            <p className="diag-done-cta">
              Ready for the next step? Reply to that email to schedule your{" "}
              <strong>Tier 03 Live All-Inclusive Reading</strong>, or apply for the{" "}
              <strong>Tier 04 8-Week Identity Mastery</strong> course.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // status === "ready" or "submitting"
  const currentPillar = PILLARS[pillarIdx];
  const qs = meta.questions[currentPillar] || [];
  const arr = responses[currentPillar] || [];
  const pctComplete = (PILLARS.reduce((acc, p) => acc + (responses[p] || []).filter(Boolean).length, 0) / 60) * 100;

  return (
    <div className="space-diag" data-testid="diag-root">
      <div className="diag-topbar">
        S.P.A.C.E. Academy · Tier 02 Diagnostic Test · Ethiopian Christ Canon
      </div>
      <div className="diag-wrap">
        <div className="diag-header">
          <div className="diag-kicker">Welcome, {meta.name}</div>
          <h1>
            The <span className="gold">S.P.A.C.E. Diagnostic</span>
          </h1>
          <p className="diag-sub">
            60 questions. Five pillars. One identity. Answer with honesty — your Identity Map PDF will be delivered to {meta.email} the moment you complete the diagnostic.
          </p>
        </div>

        {/* Pillar progress nav */}
        <div className="diag-progress" data-testid="diag-progress">
          {PILLARS.map((p, i) => (
            <div
              key={p}
              className={`diag-progress-step ${i === pillarIdx ? "active" : ""} ${isPillarComplete(p) ? "done" : ""}`}
              onClick={() => setPillarIdx(i)}
              data-testid={`diag-pillar-tab-${p}`}
            >
              <span className="diag-progress-letter">{p}</span>
              <span className="diag-progress-name">{PILLAR_NAMES[p]}</span>
            </div>
          ))}
        </div>
        <div className="diag-progress-bar">
          <div className="diag-progress-fill" style={{ width: `${pctComplete}%` }} />
        </div>

        {/* Current pillar questions */}
        <div className="diag-pillar-card" data-testid={`diag-pillar-${currentPillar}`}>
          <div className="diag-pillar-header">
            <span className="diag-pillar-letter">{currentPillar}</span>
            <div>
              <h2>{PILLAR_NAMES[currentPillar]} Diagnostic</h2>
              <p>{PILLAR_DESCRIPTIONS[currentPillar]}</p>
            </div>
          </div>

          <div className="diag-likert-legend">
            {LIKERT.map((l) => (
              <span key={l.value}>
                <strong>{l.short}</strong> · {l.label}
              </span>
            ))}
          </div>

          <div className="diag-questions">
            {qs.map((q, qi) => (
              <div className="diag-question" key={qi} data-testid={`diag-q-${currentPillar}-${qi}`}>
                <div className="diag-question-text">
                  <span className="diag-question-num">{qi + 1}.</span> {q}
                </div>
                <div className="diag-likert-row">
                  {LIKERT.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      className={`diag-likert-btn ${arr[qi] === l.value ? "selected" : ""}`}
                      onClick={() => chooseOption(currentPillar, qi, l.value)}
                      data-testid={`diag-q-${currentPillar}-${qi}-opt-${l.value}`}
                      aria-label={l.label}
                    >
                      <span className="diag-likert-short">{l.short}</span>
                      <span className="diag-likert-num">{l.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="diag-nav">
            <button
              type="button"
              className="btn-ghost"
              onClick={goPrevPillar}
              disabled={pillarIdx === 0}
              data-testid="diag-prev-btn"
            >
              ← Previous
            </button>
            {pillarIdx < PILLARS.length - 1 ? (
              <button
                type="button"
                className="btn-primary"
                onClick={goNextPillar}
                data-testid="diag-next-btn"
              >
                Next Pillar →
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={submit}
                disabled={status === "submitting"}
                data-testid="diag-submit-btn"
              >
                {status === "submitting" ? "Generating Your Map…" : "Submit & Reveal Identity Map"}
              </button>
            )}
          </div>

          {errorMsg && (
            <p className="diag-error" data-testid="diag-error-msg">
              {errorMsg}
            </p>
          )}
        </div>

        <div className="diag-footer">
          Your responses are confidential. Your Identity Map PDF is delivered exclusively to your email.
          <br />
          <em>In service and in fire — Dr. Will Rogers · Masterteacher33</em>
        </div>
      </div>
    </div>
  );
}
